import React, { useContext, FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import useApi from '../../shared/hook/useApi';
import { useAppConfiguration } from '../../shared/hook';
import { AlertContext } from '../../shared/app/AlertProvider';
import { ModalWithConfirmation } from '../../shared/modal/ModalWithConfirmation';
import Card from '../../shared/card';
import { OfferingCardProps } from './interface';
import OfferingStatus from './offering-status';
import { getBookings } from '../../../services';
import OfferingHeader from './offering-header';
import OfferingContent from './offering-content';
import { getEventLabel } from '../../../utils/event-type';

const OfferingCard: FC<OfferingCardProps> = ({
  upcomingEvent,
  userCredit = 0,
  onUpcomingEvent,
  loading,
  hideBanners,
}) => {
  const { t } = useTranslation();
  const { data: myBookings } = useQuery(['my-bookings'], getBookings);
  const isFullyBooked =
    upcomingEvent.maxParticipants <= upcomingEvent.numberOfBookings;
  const isBooked = Boolean(
    myBookings?.find(({ offeringId }) => offeringId === upcomingEvent.id),
  );

  const { withUnlimitedCredits } = useAppConfiguration();
  const { showAlert } = useContext(AlertContext);
  const [requestCredit, setRequestCredit] = React.useState(false);
  const [requestCreditApi] = useApi('requestCreditApi');

  const submitCreditRequest = () => {
    requestCreditApi({
      offeringType: upcomingEvent.type,
    }).then(() => {
      showAlert({
        severity: 'success',
        message: t('v2.global.snackbar_label_creditRequestSentOut'),
      });
      setRequestCredit(false);
    });
  };

  const onEventOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (userCredit < upcomingEvent.price) {
      setRequestCredit(true);
    } else if (onUpcomingEvent) {
      onUpcomingEvent(upcomingEvent);
    }
  };

  const showStatus = !hideBanners && (isFullyBooked || isBooked);
  return (
    <>
      <Card
        disabled={isFullyBooked}
        id={upcomingEvent?.id}
        height="562px"
        type="offering"
        baseWidth={350}
        ctaLabel={t('v2.global.button_label_seeEventDetails')}
        loading={loading}
        onClick={onEventOpen}
        statusLabel={
          showStatus && (
            <OfferingStatus isFullyBooked={isFullyBooked} isBooked={isBooked} />
          )
        }
        header={
          <OfferingHeader
            hideBanners={!!hideBanners}
            showRecommended={
              !!upcomingEvent?.isRecommended && !hideBanners && !isBooked
            }
            eventTypeLabel={getEventLabel(upcomingEvent.type)}
            offeringType={upcomingEvent.type}
          />
        }
        content={<OfferingContent upcomingEvent={upcomingEvent} />}
      />

      <ModalWithConfirmation
        onClose={() => setRequestCredit(false)}
        onCancel={() => setRequestCredit(false)}
        isOpen={requestCredit}
        onConfirm={submitCreditRequest}
        title={
          withUnlimitedCredits
            ? t(
                'v2.global.modal_unlimitedPricing_bookingTooManyOfferings_label_title',
              )
            : t('v2.global.modal_creditSystem_requestCredits_label_title')
        }
        text={
          withUnlimitedCredits
            ? t(
                'v2.global.modal_unlimitedPricing_bookingTooManyOfferings_label_body',
              )
            : t('v2.global.modal_creditSystem_requestCredits_label_body')
        }
        confirmBtnLabel={
          withUnlimitedCredits
            ? t(
                'v2.global.modal_unlimitedPricing_bookingTooManyOfferings_label_button',
              )
            : t('v2.global.modal_creditSystem_requestCredits_button_label')
        }
        cancelBtnlabel={t('v2.global.button_label_cancel')}
        snackbar={<></>}
      />
    </>
  );
};

export default OfferingCard;
